import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PhoneMockup } from "@/components/phone-mockup";
import { MacbookMockup } from "@/components/macbook-mockup";
import type { Product, Creator } from "@/lib/queries";

interface Props {
  products: Product[];
  creator?: Creator;
  /** Auto-advance interval in ms. Set to 0 to disable. */
  intervalMs?: number;
}

const allDevices = ["phone", "macbook"] as const;
const mobileDevices = ["macbook"] as const;
type Device = (typeof allDevices)[number];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

export function DeviceCarousel({ products, creator, intervalMs = 6000 }: Props) {
  const isMobile = useIsMobile();
  const devices = isMobile ? mobileDevices : allDevices;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);

  // Reset index when devices list changes
  useEffect(() => {
    setIndex(0);
  }, [isMobile]);

  // Pause auto-advance when the carousel is offscreen
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Pause when the browser tab is hidden
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (devices.length <= 1) return;
    if (!intervalMs || paused || !visible || !tabVisible) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % devices.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, paused, visible, tabVisible, devices.length]);

  const go = (dir: 1 | -1) => setIndex((i) => (i + dir + devices.length) % devices.length);
  const current: Device = devices[index];
  const hasMultiple = devices.length > 1;

  return (
    <div
      ref={rootRef}
      className="relative w-full h-full"
      style={{ contentVisibility: "auto", containIntrinsicSize: "600px 600px" } as CSSProperties}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div key={current} className="relative w-full h-full min-h-[280px] md:min-h-0 animate-fade-in py-6 px-4 flex items-center justify-center">
        {current === "phone" ? (
          <div style={{ aspectRatio: "9/19.5" }} className="h-full">
            <PhoneMockup products={products} creator={creator} />
          </div>
        ) : (
          <div style={{ aspectRatio: "16/9" }} className="h-full">
            <MacbookMockup products={products} creator={creator} />
          </div>
        )}
      </div>

      {hasMultiple && (
        <>
          {/* Controls — grouped on the right side */}
          <div className="absolute right-2 top-1/2 z-40 flex -translate-y-1/2 flex-col gap-1">
            <button
              type="button"
              aria-label="Previous device"
              onClick={() => go(-1)}
              className="rounded-full border border-border/60 bg-background/80 p-2 text-foreground shadow-md backdrop-blur transition hover:bg-background hover:scale-105"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Next device"
              onClick={() => go(1)}
              className="rounded-full border border-border/60 bg-background/80 p-2 text-foreground shadow-md backdrop-blur transition hover:bg-background hover:scale-105"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Dots */}
          <div className="absolute bottom-2 left-1/2 z-40 flex -translate-x-1/2 gap-0.5">
            {devices.map((d, i) => (
              <button
                key={d}
                type="button"
                aria-label={`Show ${d}`}
                aria-current={i === index}
                onClick={() => setIndex(i)}
                className="group flex h-11 w-11 items-center justify-center"
              >
                <span
                  aria-hidden="true"
                  className={`block h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-foreground" : "w-1.5 bg-foreground/30 group-hover:bg-foreground/60"}`}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
